/**
 * Plan Workbench — six-sheet plan-cycle workbench over the governance tables
 * (Planning canon 10–17). Formerly "Planning Spreadsheet"; route stays
 * /planning/spreadsheet.
 *
 * Sheets:
 *   Overview     → system explanation + active cycle health
 *   Priorities   → prioritization input boundary (read-only; flag for re-prioritization flows back)
 *   Work Units   → atomic execution inventory (add / edit)
 *   Dependencies → constraint graph (add)
 *   Capacity     → feasibility model (add)
 *   Plan of Record → committed work with effort rollup
 *
 * Bidirectional flow:
 *   Prioritization → Planning  via plan cycle input snapshot (items / item pools / priority lists)
 *   Planning → Prioritization  via recommendReprioritization when constraint pressure is flagged
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Target,
  Layers,
  GitBranch,
  Users,
  ClipboardList,
  Plus,
  Loader2,
  AlertTriangle,
  AlertCircle,
  ArrowUpRight,
  RefreshCw,
  Check,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { NoPlanCycleState } from '../../components/planning/NoPlanCycleState';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { getSupabaseClient } from '../../lib/supabaseClient';
import {
  getFirstActiveCycleProvisional,
  getWorkUnitsForPlanVersion,
  createWorkUnit,
  createDependencyLink,
  createCapacityAllocation,
  getItemTitlesByIds,
  recommendReprioritization,
  syncItemDependenciesToPlan,
} from '../../lib/planningService';
import { DEPENDENCY_TIMING_LABELS } from '../../lib/sequenceRelationships';
import type {
  PlanCycle,
  WorkUnit,
  DependencyLink,
  CapacityAllocation,
  Initiative,
  WorkUnitReadinessState,
  DependencyTypeEnum,
} from '../../types/planning';
import { RankMark } from '@/components/prioritization/RankMark';

// ─── Types ───────────────────────────────────────────────────────────────────

type SheetId = 'overview' | 'priorities' | 'work-units' | 'dependencies' | 'capacity' | 'por';

interface SheetDef {
  id: SheetId;
  label: string;
  icon: React.FC<{ className?: string }>;
  subtitle: string;
}

interface PriorityRow {
  id: string;
  rank: number;
  title: string;
  itemId: string;
  initiativeId: string;
  workProfile: string | null;
  status: string;
  effortHours: number;
}

// ─── Sheet definitions ────────────────────────────────────────────────────────

const SHEETS: SheetDef[] = [
  { id: 'overview', label: 'Overview', icon: BookOpen, subtitle: 'How this system works' },
  { id: 'priorities', label: 'Priorities', icon: Target, subtitle: 'Desired outcomes (input boundary)' },
  { id: 'work-units', label: 'Work Units', icon: Layers, subtitle: 'Execution inventory' },
  { id: 'dependencies', label: 'Dependencies', icon: GitBranch, subtitle: 'Constraints & blockers' },
  { id: 'capacity', label: 'Capacity Model', icon: Users, subtitle: 'Feasibility check' },
  { id: 'por', label: 'Plan of Record', icon: ClipboardList, subtitle: 'Committed work' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const READINESS_COLORS: Record<WorkUnitReadinessState, string> = {
  not_ready: 'bg-[var(--coral-dim)] text-[var(--coral)]',
  ready: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  in_progress: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  done: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  dropped: 'bg-[var(--surface)] text-[var(--text-ter)]',
};

const DEP_TYPE_LABEL: Record<DependencyTypeEnum, string> = {
  FS: DEPENDENCY_TIMING_LABELS.FS.short,
  SS: DEPENDENCY_TIMING_LABELS.SS.short,
  FF: DEPENDENCY_TIMING_LABELS.FF.short,
  SF: DEPENDENCY_TIMING_LABELS.SF.short,
};

function Badge({ label, className }: { label: string; className?: string }) {
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium leading-none ${className}`}>
      {label}
    </span>
  );
}

function SheetCell({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] whitespace-nowrap ${className}`}>{children}</td>;
}

function SheetHeader({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] whitespace-nowrap ${className}`}>
      {children}
    </th>
  );
}

function EmptySheet({ message }: { message: string }) {
  return (
    <tr>
      <td colSpan={20} className="px-4 py-8 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        {message}
      </td>
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PlanningSpreadsheet() {
  const [activeSheet, setActiveSheet] = useState<SheetId>('overview');
  const [cycle, setCycle] = useState<PlanCycle | null>(null);
  const [planVersionId, setPlanVersionId] = useState<string | null>(null);
  const [editingPlanVersionId, setEditingPlanVersionId] = useState<string | null>(null);
  const [isProvisional, setIsProvisional] = useState(false);
  const [loadingCycle, setLoadingCycle] = useState(true);
  const [isNonActiveCycle, setIsNonActiveCycle] = useState(false);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);

  // Per-sheet data
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [itemTitles, setItemTitles] = useState<Record<string, string>>({});
  const [workUnits, setWorkUnits] = useState<WorkUnit[]>([]);
  const [depLinks, setDepLinks] = useState<DependencyLink[]>([]);
  const [capacityRows, setCapacityRows] = useState<CapacityAllocation[]>([]);
  const [sheetLoading, setSheetLoading] = useState(false);

  // Add-row state
  const [addingWu, setAddingWu] = useState(false);
  const [newWuTitle, setNewWuTitle] = useState('');
  const [newWuInitiativeId, setNewWuInitiativeId] = useState('');
  const [newWuEffort, setNewWuEffort] = useState('');
  const [newWuProfile, setNewWuProfile] = useState('');
  const [savingWu, setSavingWu] = useState(false);
  const [wuError, setWuError] = useState<string | null>(null);

  const [addingDep, setAddingDep] = useState(false);
  const [newDepFrom, setNewDepFrom] = useState('');
  const [newDepTo, setNewDepTo] = useState('');
  const [newDepType, setNewDepType] = useState<DependencyTypeEnum>('FS');
  const [savingDep, setSavingDep] = useState(false);
  const [depError, setDepError] = useState<string | null>(null);
  const [importingItemDeps, setImportingItemDeps] = useState(false);
  const [importDepsMessage, setImportDepsMessage] = useState<string | null>(null);

  const [addingCap, setAddingCap] = useState(false);
  const [newCapResource, setNewCapResource] = useState('');
  const [newCapPct, setNewCapPct] = useState('');
  const [newCapStart, setNewCapStart] = useState('');
  const [newCapEnd, setNewCapEnd] = useState('');
  const [savingCap, setSavingCap] = useState(false);
  const [capError, setCapError] = useState<string | null>(null);

  const [flaggingItemId, setFlaggingItemId] = useState<string | null>(null);
  const [flaggedItemIds, setFlaggedItemIds] = useState<Set<string>>(new Set());
  const [expandedWuId, setExpandedWuId] = useState<string | null>(null);

  // ── Load active cycle (provisional: POR not required) ─────────────────────

  useEffect(() => {
    setLoadingCycle(true);
    getFirstActiveCycleProvisional(selectedCycleId ? { cycleId: selectedCycleId } : undefined).then(({ data }) => {
      if (!data) {
        setLoadingCycle(false);
        return;
      }
      setCycle(data.cycle);
      setPlanVersionId(data.planVersionId);
      setEditingPlanVersionId(data.editingPlanVersionId);
      setIsProvisional(data.isProvisional);
      setIsNonActiveCycle(data.isNonActiveCycle ?? false);
      setLoadingCycle(false);
    });
  }, [selectedCycleId]);

  // ── Load sheet data when tab changes ─────────────────────────────────────

  const loadSheetData = useCallback(
    async (sheet: SheetId) => {
      if (!planVersionId) return;
      setSheetLoading(true);
      const supabase = getSupabaseClient();

      try {
        if (sheet === 'priorities' || sheet === 'por') {
          const { data: initRows } = await supabase
            .from('initiatives')
            .select('*')
            .eq('plan_version_id', planVersionId)
            .order('priority_position', { ascending: true });
          const inits = (initRows ?? []) as Initiative[];
          setInitiatives(inits);

          const itemIds = inits.map((i) => i.item_id).filter(Boolean);
          if (itemIds.length > 0) {
            const { data: titles } = await getItemTitlesByIds(itemIds as string[]);
            setItemTitles(titles ?? {});
          }

          if (sheet === 'por') {
            const { data: wus } = await getWorkUnitsForPlanVersion(planVersionId);
            setWorkUnits(wus ?? []);
          }
        }

        if (sheet === 'work-units') {
          const { data: wus } = await getWorkUnitsForPlanVersion(planVersionId);
          setWorkUnits(wus ?? []);
          const { data: initRows } = await supabase
            .from('initiatives')
            .select('id, item_id, title, priority_position')
            .eq('plan_version_id', planVersionId)
            .order('priority_position', { ascending: true });
          const inits = (initRows ?? []) as Initiative[];
          setInitiatives(inits);
        }

        if (sheet === 'dependencies') {
          const { data: initRows } = await supabase
            .from('initiatives')
            .select('id')
            .eq('plan_version_id', planVersionId);
          const initiativeIds = (initRows ?? []).map((r: { id: string }) => r.id);
          if (initiativeIds.length > 0) {
            const { data: wuRows } = await supabase
              .from('work_units')
              .select('id')
              .in('initiative_id', initiativeIds);
            const wuIds = (wuRows ?? []).map((r: { id: string }) => r.id);
            if (wuIds.length > 0) {
              const { data: links } = await supabase
                .from('dependency_links')
                .select('*')
                .or(`from_work_unit_id.in.(${wuIds.join(',')}),to_work_unit_id.in.(${wuIds.join(',')})`);
              setDepLinks((links ?? []) as DependencyLink[]);
              const { data: wus } = await getWorkUnitsForPlanVersion(planVersionId);
              setWorkUnits(wus ?? []);
            } else {
              setDepLinks([]);
            }
          } else {
            setDepLinks([]);
          }
        }

        if (sheet === 'capacity') {
          const { data: capRows } = await supabase
            .from('capacity_allocations')
            .select('*')
            .eq('plan_version_id', planVersionId)
            .order('resource_identifier', { ascending: true });
          setCapacityRows((capRows ?? []) as CapacityAllocation[]);
        }
      } finally {
        setSheetLoading(false);
      }
    },
    [planVersionId]
  );

  useEffect(() => {
    if (!planVersionId) return;
    loadSheetData(activeSheet);
  }, [activeSheet, planVersionId, loadSheetData]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const wuByInitiative = useMemo(() => {
    const map = new Map<string, WorkUnit[]>();
    for (const wu of workUnits) {
      const arr = map.get(wu.initiative_id) ?? [];
      arr.push(wu);
      map.set(wu.initiative_id, arr);
    }
    return map;
  }, [workUnits]);

  const wuById = useMemo(() => {
    const map = new Map<string, WorkUnit>();
    for (const wu of workUnits) map.set(wu.id, wu);
    return map;
  }, [workUnits]);

  const priorityRows: PriorityRow[] = useMemo(
    () =>
      initiatives.map((init, idx) => ({
        id: init.id,
        rank: init.priority_position ?? idx + 1,
        title: init.title ?? (init.item_id ? itemTitles[init.item_id] : null) ?? '—',
        itemId: init.item_id ?? '',
        initiativeId: init.id,
        workProfile: init.work_profile,
        status: init.status,
        effortHours: (wuByInitiative.get(init.id) ?? []).reduce(
          (sum, wu) => sum + (wu.estimated_effort_hours ?? 0),
          0
        ),
      })),
    [initiatives, itemTitles, wuByInitiative]
  );

  const totalCapacityHours = useMemo(
    () =>
      capacityRows.reduce((sum, r) => sum + r.allocation_percentage, 0),
    [capacityRows]
  );

  const committedEffortHours = useMemo(
    () => workUnits.reduce((sum, wu) => sum + (wu.estimated_effort_hours ?? 0), 0),
    [workUnits]
  );

  // ── Actions ───────────────────────────────────────────────────────────────

  async function handleAddWorkUnit() {
    if (!newWuTitle.trim() || !newWuInitiativeId) return;
    setSavingWu(true);
    setWuError(null);
    const { error } = await createWorkUnit(newWuInitiativeId, {
      title: newWuTitle.trim(),
      estimated_effort_hours: newWuEffort ? parseFloat(newWuEffort) : null,
      work_profile: newWuProfile || null,
    });
    if (error) { setWuError(error); setSavingWu(false); return; }
    setNewWuTitle('');
    setNewWuInitiativeId('');
    setNewWuEffort('');
    setNewWuProfile('');
    setAddingWu(false);
    setSavingWu(false);
    loadSheetData('work-units');
  }

  async function handleAddDependency() {
    if (!newDepFrom || !newDepTo) return;
    const versionId = editingPlanVersionId ?? planVersionId;
    if (!versionId) return;
    setSavingDep(true);
    setDepError(null);
    const { error } = await createDependencyLink(versionId, newDepFrom, newDepTo, newDepType);
    if (error) { setDepError(error); setSavingDep(false); return; }
    setNewDepFrom('');
    setNewDepTo('');
    setNewDepType('FS');
    setAddingDep(false);
    setSavingDep(false);
    loadSheetData('dependencies');
  }

  async function handleImportItemDependencies() {
    const versionId = editingPlanVersionId ?? planVersionId;
    if (!versionId) return;
    setImportingItemDeps(true);
    setImportDepsMessage(null);
    setDepError(null);
    const { data, error } = await syncItemDependenciesToPlan(versionId);
    setImportingItemDeps(false);
    if (error) {
      setDepError(error);
      return;
    }
    if (data) {
      const parts = [
        `${data.created} sequence link${data.created !== 1 ? 's' : ''} created`,
        data.skipped > 0 ? `${data.skipped} skipped` : null,
        data.rollupWorkUnitsCreated > 0 ? `${data.rollupWorkUnitsCreated} rollup work unit(s) created` : null,
        data.mirrorPairs > 0 ? `${data.mirrorPairs} mirror pair(s) noted` : null,
      ].filter(Boolean);
      setImportDepsMessage(parts.join(' · '));
      if (data.mirrorPairs > 0) {
        setImportDepsMessage((prev) => `${prev} — review mirror pairs in Relationship Health`);
      }
    }
    loadSheetData('dependencies');
    loadSheetData('work-units');
  }

  async function handleAddCapacity() {
    if (!newCapResource || !newCapPct || !editingPlanVersionId) return;
    setSavingCap(true);
    setCapError(null);
    const { error } = await createCapacityAllocation(editingPlanVersionId, {
      resource_identifier: newCapResource.trim(),
      allocation_percentage: parseFloat(newCapPct),
      time_window_start: newCapStart || null,
      time_window_end: newCapEnd || null,
    });
    if (error) { setCapError(error); setSavingCap(false); return; }
    setNewCapResource('');
    setNewCapPct('');
    setNewCapStart('');
    setNewCapEnd('');
    setAddingCap(false);
    setSavingCap(false);
    loadSheetData('capacity');
  }

  async function handleFlagForReprioritization(row: PriorityRow) {
    if (!cycle || flaggedItemIds.has(row.itemId)) return;
    setFlaggingItemId(row.itemId);
    await recommendReprioritization(cycle.id, {
      reason: 'planning_constraint_pressure',
      note: `Flagged from Plan Workbench: "${row.title}" may need re-prioritization due to planning constraint pressure.`,
    });
    setFlaggedItemIds((prev) => new Set([...prev, row.itemId]));
    setFlaggingItemId(null);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loadingCycle) {
    return (
      <div className="flex items-center gap-3 py-16 justify-center text-[var(--text-ter)]">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading plan…</span>
      </div>
    );
  }

  if (!cycle) {
    return (
      <div className="max-w-xl py-12">
        <PageHeader
          title="Plan Workbench"
          subtitle="Select a plan cycle to get started"
          icon={<ClipboardList className="h-5 w-5 text-primary-600" />}
        />
        <div className="mt-6">
          <NoPlanCycleState onSelectCycle={(id) => { setSelectedCycleId(id); setLoadingCycle(true); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full">
      <PageHeader
        title="Plan Workbench"
        subtitle={cycle.name}
        icon={<ClipboardList className="h-5 w-5 text-primary-600" />}
        actions={
          <div className="flex items-center gap-2">
            {isProvisional && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 px-3 py-1 text-xs font-medium text-[var(--mustard)] dark:text-[var(--mustard)] border border-[var(--mustard)] dark:border-[var(--mustard)]/40">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)] dark:bg-[var(--mustard)]" />
                Provisional Plan
              </span>
            )}
            <button
              type="button"
              onClick={() => loadSheetData(activeSheet)}
              className="inline-flex items-center gap-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        }
      />
      {isNonActiveCycle && (
        <div className="mt-3 flex items-center gap-2 border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-2.5 text-sm text-[var(--mustard)]">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Viewing a non-active plan cycle. Activate it from{' '}
          <Link to="/planning/plans" className="font-medium underline">Planning Home</Link> when ready.
        </div>
      )}
      {!isNonActiveCycle && isProvisional && (
        <div className="mt-3 border border-[var(--mustard)] dark:border-[var(--mustard)]/40 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/10 px-4 py-2.5 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
          No approved Plan of Record exists for this cycle. Planning is available provisionally — decisions made here are not yet authoritative.
        </div>
      )}

      {/* Sheet tab bar */}
      <div className="mt-4 flex border-b border-[var(--rule)] dark:border-[var(--rule)] gap-0 overflow-x-auto">
        {SHEETS.map((sheet) => {
          const active = activeSheet === sheet.id;
          return (
            <button
              key={sheet.id}
              type="button"
              onClick={() => setActiveSheet(sheet.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                active
                  ? 'border-primary-600 text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/15'
                  : 'border-transparent text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
              }`}
            >
              <sheet.icon className="h-3.5 w-3.5" />
              {sheet.label}
            </button>
          );
        })}
      </div>

      {/* Sheet subtitle bar */}
      <div className="flex items-center justify-between px-1 py-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        <span>{SHEETS.find((s) => s.id === activeSheet)?.subtitle}</span>
        {sheetLoading && (
          <span className="flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </span>
        )}
      </div>

      {/* Sheet content */}
      <div className="mt-1">
        {activeSheet === 'overview' && <OverviewSheet cycle={cycle} planVersionId={planVersionId} />}
        {activeSheet === 'priorities' && (
          <PrioritiesSheet
            rows={priorityRows}
            loading={sheetLoading}
            flaggingItemId={flaggingItemId}
            flaggedItemIds={flaggedItemIds}
            onFlag={handleFlagForReprioritization}
            cycle={cycle}
          />
        )}
        {activeSheet === 'work-units' && (
          <WorkUnitsSheet
            workUnits={workUnits}
            initiatives={initiatives}
            itemTitles={itemTitles}
            expandedWuId={expandedWuId}
            onToggleExpand={(id) => setExpandedWuId((prev) => (prev === id ? null : id))}
            loading={sheetLoading}
            addingWu={addingWu}
            newWuTitle={newWuTitle}
            newWuInitiativeId={newWuInitiativeId}
            newWuEffort={newWuEffort}
            newWuProfile={newWuProfile}
            savingWu={savingWu}
            wuError={wuError}
            setAddingWu={setAddingWu}
            setNewWuTitle={setNewWuTitle}
            setNewWuInitiativeId={setNewWuInitiativeId}
            setNewWuEffort={setNewWuEffort}
            setNewWuProfile={setNewWuProfile}
            onSaveWu={handleAddWorkUnit}
          />
        )}
        {activeSheet === 'dependencies' && (
          <DependenciesSheet
            links={depLinks}
            wuById={wuById}
            workUnits={workUnits}
            loading={sheetLoading}
            addingDep={addingDep}
            newDepFrom={newDepFrom}
            newDepTo={newDepTo}
            newDepType={newDepType}
            savingDep={savingDep}
            depError={depError}
            setAddingDep={setAddingDep}
            setNewDepFrom={setNewDepFrom}
            setNewDepTo={setNewDepTo}
            setNewDepType={setNewDepType}
            onSaveDep={handleAddDependency}
            canEdit={!!editingPlanVersionId}
            importingItemDeps={importingItemDeps}
            importDepsMessage={importDepsMessage}
            onImportItemDeps={handleImportItemDependencies}
          />
        )}
        {activeSheet === 'capacity' && (
          <CapacitySheet
            rows={capacityRows}
            totalCapacityHours={totalCapacityHours}
            committedEffortHours={committedEffortHours}
            loading={sheetLoading}
            addingCap={addingCap}
            newCapResource={newCapResource}
            newCapPct={newCapPct}
            newCapStart={newCapStart}
            newCapEnd={newCapEnd}
            savingCap={savingCap}
            capError={capError}
            setAddingCap={setAddingCap}
            setNewCapResource={setNewCapResource}
            setNewCapPct={setNewCapPct}
            setNewCapStart={setNewCapStart}
            setNewCapEnd={setNewCapEnd}
            onSaveCap={handleAddCapacity}
          />
        )}
        {activeSheet === 'por' && (
          <PlanOfRecordSheet
            initiatives={initiatives}
            itemTitles={itemTitles}
            wuByInitiative={wuByInitiative}
            loading={sheetLoading}
            cycle={cycle}
            planVersionId={planVersionId}
          />
        )}
      </div>
    </div>
  );
}

// ─── Sheet: Overview ──────────────────────────────────────────────────────────

function OverviewSheet({ cycle, planVersionId }: { cycle: PlanCycle; planVersionId: string | null }) {
  const layers = [
    {
      sheet: 'Priorities',
      role: 'Defines desired outcomes — the force-ranked importance from the prioritization domain. Read-only input boundary; planning never rewrites this.',
      icon: Target,
      link: '/prioritization/reports/cycles',
      linkLabel: 'Open cycle reports',
      direction: '→ feeds into planning',
    },
    {
      sheet: 'Work Units',
      role: 'Inventories atomic execution units derived from committed initiatives. Each work unit belongs to one initiative (priority) and carries effort, duration, state, and readiness signals.',
      icon: Layers,
      link: '/planning/work-graph',
      linkLabel: 'Work Graph',
      direction: '→ drives dependencies & capacity',
    },
    {
      sheet: 'Dependencies',
      role: 'Reveals structural constraints — which work units must finish before others can start. Acyclicity enforced. Blockers surface in readiness evaluation.',
      icon: GitBranch,
      link: '/planning/work-graph',
      linkLabel: 'Work Graph',
      direction: '→ blocks scheduling',
    },
    {
      sheet: 'Capacity Model',
      role: 'Tests feasibility — maps owners and roles to allocation percentages and time windows. Constraint evaluations check whether committed effort fits available capacity.',
      icon: Users,
      link: '/planning/capacity',
      linkLabel: 'Capacity view',
      direction: '→ gates commitment',
    },
    {
      sheet: 'Plan of Record',
      role: 'Captures the authorized commitment — which priorities are in, their total effort, and what was traded off. One active POR per cycle. All changes to POR are versioned.',
      icon: ClipboardList,
      link: '/planning/plan-of-record',
      linkLabel: 'Full POR view',
      direction: '← authorized output',
    },
  ];

  return (
    <div className="max-w-3xl space-y-6 py-2">
      {/* Active cycle card */}
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] flex items-center justify-between">
          <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Active plan cycle</span>
          <Badge label={cycle.status} className="bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]" />
        </div>
        <div className="px-5 py-3 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs uppercase tracking-wide mb-1">Cycle name</p>
            <p className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{cycle.name}</p>
          </div>
          <div>
            <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs uppercase tracking-wide mb-1">Input type</p>
            <p className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">{cycle.input_type.replace(/_/g, ' ')}</p>
          </div>
          <div>
            <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs uppercase tracking-wide mb-1">Plan version</p>
            <p className="font-mono text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{planVersionId?.slice(0, 12) ?? '—'}</p>
          </div>
          <div>
            <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs uppercase tracking-wide mb-1">Started</p>
            <p className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">{new Date(cycle.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* System diagram */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
          How this system works
        </p>
        <div className="space-y-2">
          {layers.map((layer) => (
            <div
              key={layer.sheet}
              className="flex items-start gap-4 border border-[var(--rule)] dark:border-[var(--rule)] px-4 py-3"
            >
              <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)] mt-0.5">
                <layer.icon className="h-4 w-4 text-[var(--text-sec)] dark:text-[var(--text-sec)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{layer.sheet}</span>
                  <span className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">{layer.direction}</span>
                </div>
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-0.5 leading-relaxed">{layer.role}</p>
              </div>
              <Link
                to={layer.link}
                className="flex-shrink-0 flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline mt-0.5"
              >
                {layer.linkLabel}
                <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Bidirectional note */}
      <div className="border border-[var(--slate)] dark:border-[var(--slate)]/40 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/10 px-4 py-3 text-sm text-[var(--text-sec)]">
        <strong>Bidirectional flow:</strong> Priorities flow in from the prioritization domain as a read-only input snapshot.
        Planning signals — constraint pressure, effort discoveries, new items surfaced during execution — flow back to prioritization
        via reprioritization recommendations. Use the <strong>Priorities</strong> sheet to flag items.
      </div>
    </div>
  );
}

// ─── Sheet: Priorities ────────────────────────────────────────────────────────

function PrioritiesSheet({
  rows,
  loading,
  flaggingItemId,
  flaggedItemIds,
  onFlag,
  cycle,
}: {
  rows: PriorityRow[];
  loading: boolean;
  flaggingItemId: string | null;
  flaggedItemIds: Set<string>;
  onFlag: (row: PriorityRow) => void;
  cycle: PlanCycle;
}) {
  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] flex items-center justify-between">
        <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">
          Priorities — input snapshot for "{cycle.name}"
        </span>
        <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Read-only · {rows.length} items</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <SheetHeader className="w-12">#</SheetHeader>
              <SheetHeader>Initiative / Priority</SheetHeader>
              <SheetHeader>Work Profile</SheetHeader>
              <SheetHeader>Status</SheetHeader>
              <SheetHeader className="text-right">Effort (hrs)</SheetHeader>
              <SheetHeader className="w-40">Flow back</SheetHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {loading ? (
              <EmptySheet message="Loading priorities…" />
            ) : rows.length === 0 ? (
              <EmptySheet message="No initiatives in this plan version yet. Create a plan of record to see priorities here." />
            ) : (
              rows.map((row) => {
                const flagged = flaggedItemIds.has(row.itemId);
                const flagging = flaggingItemId === row.itemId;
                return (
                  <tr key={row.id} className="hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]">
                    <SheetCell>
                      {/* The plan version's own order — the official rank is recorded only when a plan is authorized */}
                      <RankMark rank={row.rank} official={false} size="sm" />
                    </SheetCell>
                    <SheetCell>
                      <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{row.title}</span>
                    </SheetCell>
                    <SheetCell>
                      {row.workProfile ? (
                        <Badge label={row.workProfile} className="bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]" />
                      ) : (
                        <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">—</span>
                      )}
                    </SheetCell>
                    <SheetCell>
                      <Badge
                        label={row.status}
                        className={
                          row.status === 'in_progress'
                            ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                            : row.status === 'complete'
                            ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                            : row.status === 'dropped'
                            ? 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
                            : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                        }
                      />
                    </SheetCell>
                    <SheetCell className="text-right font-mono tabular-nums text-[var(--text-sec)]">
                      {row.effortHours > 0 ? row.effortHours.toFixed(1) : '—'}
                    </SheetCell>
                    <SheetCell>
                      {flagged ? (
                        <span className="flex items-center gap-1 text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Flagged for re-prioritization
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onFlag(row)}
                          disabled={!row.itemId || flagging}
                          className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--mustard)] dark:hover:text-[var(--mustard)] disabled:opacity-40 transition-colors"
                          title="Flag this priority for re-prioritization due to planning constraint pressure"
                        >
                          {flagging ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <AlertTriangle className="h-3.5 w-3.5" />
                          )}
                          Flag for re-priority
                        </button>
                      )}
                    </SheetCell>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]">
        <Link
          to="/prioritization/reports/cycles"
          className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
        >
          Manage priorities in Prioritization <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

// ─── Sheet: Work Units ────────────────────────────────────────────────────────

function WorkUnitsSheet({
  workUnits,
  initiatives,
  itemTitles,
  expandedWuId,
  onToggleExpand,
  loading,
  addingWu,
  newWuTitle,
  newWuInitiativeId,
  newWuEffort,
  newWuProfile,
  savingWu,
  wuError,
  setAddingWu,
  setNewWuTitle,
  setNewWuInitiativeId,
  setNewWuEffort,
  setNewWuProfile,
  onSaveWu,
}: {
  workUnits: WorkUnit[];
  initiatives: Initiative[];
  itemTitles: Record<string, string>;
  expandedWuId: string | null;
  onToggleExpand: (id: string) => void;
  loading: boolean;
  addingWu: boolean;
  newWuTitle: string;
  newWuInitiativeId: string;
  newWuEffort: string;
  newWuProfile: string;
  savingWu: boolean;
  wuError: string | null;
  setAddingWu: (v: boolean) => void;
  setNewWuTitle: (v: string) => void;
  setNewWuInitiativeId: (v: string) => void;
  setNewWuEffort: (v: string) => void;
  setNewWuProfile: (v: string) => void;
  onSaveWu: () => void;
}) {
  const initiativeLabel = (initId: string) => {
    const init = initiatives.find((i) => i.id === initId);
    if (!init) return initId.slice(0, 8);
    const title = init.title ?? (init.item_id ? itemTitles[init.item_id] : null);
    return title ? `#${init.priority_position ?? '?'} ${title}` : `Initiative ${initId.slice(0, 8)}`;
  };

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] flex items-center justify-between">
        <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">
          Work Units — {workUnits.length} items
        </span>
        <button
          type="button"
          onClick={() => setAddingWu(true)}
          disabled={addingWu || initiatives.length === 0}
          className="flex items-center gap-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" />
          Add work unit
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <SheetHeader className="w-6" />
              <SheetHeader>Title</SheetHeader>
              <SheetHeader>Initiative</SheetHeader>
              <SheetHeader>Profile</SheetHeader>
              <SheetHeader className="text-right">Est. hours</SheetHeader>
              <SheetHeader className="text-right">Est. days</SheetHeader>
              <SheetHeader>State</SheetHeader>
              <SheetHeader>Ready</SheetHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {loading ? (
              <EmptySheet message="Loading work units…" />
            ) : workUnits.length === 0 && !addingWu ? (
              <EmptySheet message="No work units yet. Add initiatives via Plan of Record, then add work units here." />
            ) : (
              <>
                {workUnits.map((wu) => (
                  <>
                    <tr
                      key={wu.id}
                      className="hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] cursor-pointer"
                      onClick={() => onToggleExpand(wu.id)}
                    >
                      <SheetCell>
                        {expandedWuId === wu.id ? (
                          <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                        )}
                      </SheetCell>
                      <SheetCell>
                        <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{wu.title}</span>
                      </SheetCell>
                      <SheetCell>
                        <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs">
                          {initiativeLabel(wu.initiative_id)}
                        </span>
                      </SheetCell>
                      <SheetCell>
                        <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs">—</span>
                      </SheetCell>
                      <SheetCell className="text-right font-mono tabular-nums text-[var(--text-sec)]">
                        {wu.estimated_effort_hours ?? '—'}
                      </SheetCell>
                      <SheetCell className="text-right font-mono tabular-nums text-[var(--text-sec)]">
                        {wu.estimated_duration_days ?? '—'}
                      </SheetCell>
                      <SheetCell>
                        <Badge
                          label={wu.readiness_state.replace(/_/g, ' ')}
                          className={READINESS_COLORS[wu.readiness_state]}
                        />
                      </SheetCell>
                      <SheetCell>
                        {wu.readiness_state === 'ready' || wu.readiness_state === 'in_progress' || wu.readiness_state === 'done' ? (
                          <Check className="h-3.5 w-3.5 text-[var(--green)]" />
                        ) : (
                          <X className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                        )}
                      </SheetCell>
                    </tr>
                    {expandedWuId === wu.id && (
                      <tr key={`${wu.id}-detail`} className="bg-[var(--surface)] dark:bg-[var(--bg-raised)]">
                        <td colSpan={8} className="px-6 py-3 text-sm">
                          <div className="grid grid-cols-3 gap-4 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-[var(--text-ter)] mb-0.5">Description</p>
                              <p>{wu.description ?? '—'}</p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-[var(--text-ter)] mb-0.5">Complexity</p>
                              <p>{wu.complexity_score ?? '—'}</p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-[var(--text-ter)] mb-0.5">Risk</p>
                              <p>{wu.risk_score ?? '—'}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
                {addingWu && (
                  <tr className="bg-primary-50 dark:bg-primary-900/10">
                    <SheetCell />
                    <SheetCell>
                      <input
                        autoFocus
                        placeholder="Work unit title"
                        value={newWuTitle}
                        onChange={(e) => setNewWuTitle(e.target.value)}
                        className="w-full border-b border-[var(--rule)] dark:border-[var(--rule)] bg-transparent py-0.5 text-sm focus:outline-none focus:border-primary-500"
                      />
                    </SheetCell>
                    <SheetCell>
                      <select
                        value={newWuInitiativeId}
                        onChange={(e) => setNewWuInitiativeId(e.target.value)}
                        className="border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-0.5 text-xs bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]"
                      >
                        <option value="">Select initiative</option>
                        {initiatives.map((init) => (
                          <option key={init.id} value={init.id}>
                            {init.title ?? `Initiative ${init.id.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </SheetCell>
                    <SheetCell>
                      <input
                        placeholder="Profile"
                        value={newWuProfile}
                        onChange={(e) => setNewWuProfile(e.target.value)}
                        className="w-24 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-transparent py-0.5 text-xs focus:outline-none"
                      />
                    </SheetCell>
                    <SheetCell className="text-right">
                      <input
                        type="number"
                        placeholder="hrs"
                        value={newWuEffort}
                        onChange={(e) => setNewWuEffort(e.target.value)}
                        className="w-16 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-transparent py-0.5 text-xs text-right focus:outline-none"
                      />
                    </SheetCell>
                    <SheetCell />
                    <SheetCell colSpan={2}>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={onSaveWu}
                          disabled={savingWu || !newWuTitle.trim() || !newWuInitiativeId}
                          className="flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800 disabled:opacity-40"
                        >
                          {savingWu ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setAddingWu(false)}
                          className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                        >
                          <X className="h-3.5 w-3.5" />
                          Cancel
                        </button>
                      </div>
                      {wuError && <p className="mt-1 text-xs text-[var(--coral)]">{wuError}</p>}
                    </SheetCell>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Sheet: Dependencies ──────────────────────────────────────────────────────

function DependenciesSheet({
  links,
  wuById,
  workUnits,
  loading,
  addingDep,
  newDepFrom,
  newDepTo,
  newDepType,
  savingDep,
  depError,
  setAddingDep,
  setNewDepFrom,
  setNewDepTo,
  setNewDepType,
  onSaveDep,
  canEdit,
  importingItemDeps,
  importDepsMessage,
  onImportItemDeps,
}: {
  links: DependencyLink[];
  wuById: Map<string, WorkUnit>;
  workUnits: WorkUnit[];
  loading: boolean;
  addingDep: boolean;
  newDepFrom: string;
  newDepTo: string;
  newDepType: DependencyTypeEnum;
  savingDep: boolean;
  depError: string | null;
  setAddingDep: (v: boolean) => void;
  setNewDepFrom: (v: string) => void;
  setNewDepTo: (v: string) => void;
  setNewDepType: (v: DependencyTypeEnum) => void;
  onSaveDep: () => void;
  canEdit: boolean;
  importingItemDeps: boolean;
  importDepsMessage: string | null;
  onImportItemDeps: () => void;
}) {
  // Finish → Start covers almost every link; the other three wait behind a link.
  const [showTimingOptions, setShowTimingOptions] = useState(false);
  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">
          Sequence links — {links.length}
        </span>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={onImportItemDeps}
              disabled={importingItemDeps}
              className="flex items-center gap-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-40"
            >
              {importingItemDeps ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Import hard deps from items
            </button>
          )}
          <button
            type="button"
            onClick={() => setAddingDep(true)}
            disabled={addingDep || workUnits.length < 2 || !canEdit}
            className="flex items-center gap-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
            Add sequence link
          </button>
        </div>
      </div>
      {importDepsMessage && (
        <p className="px-4 py-2 text-xs text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          {importDepsMessage}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <SheetHeader>Predecessor (prerequisite)</SheetHeader>
              <SheetHeader className="w-40">Timing</SheetHeader>
              <SheetHeader>Dependent (successor)</SheetHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {loading ? (
              <EmptySheet message="Loading dependencies…" />
            ) : links.length === 0 && !addingDep ? (
              <EmptySheet message="No sequence links yet. Import hard dependencies from items or add links between work units." />
            ) : (
              <>
                {links.map((link) => (
                  <tr key={link.id} className="hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]">
                    <SheetCell>
                      <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                        {wuById.get(link.from_work_unit_id)?.title ?? link.from_work_unit_id.slice(0, 12)}
                      </span>
                    </SheetCell>
                    <SheetCell>
                      <Badge
                        label={DEP_TYPE_LABEL[link.dependency_type as DependencyTypeEnum]}
                        className="bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
                      />
                    </SheetCell>
                    <SheetCell>
                      <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                        {wuById.get(link.to_work_unit_id)?.title ?? link.to_work_unit_id.slice(0, 12)}
                      </span>
                    </SheetCell>
                  </tr>
                ))}
                {addingDep && (
                  <tr className="bg-primary-50 dark:bg-primary-900/10">
                    <SheetCell>
                      <select
                        autoFocus
                        value={newDepFrom}
                        onChange={(e) => setNewDepFrom(e.target.value)}
                        className="border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-0.5 text-xs bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] w-full max-w-xs"
                      >
                        <option value="">From work unit…</option>
                        {workUnits.map((wu) => (
                          <option key={wu.id} value={wu.id}>{wu.title}</option>
                        ))}
                      </select>
                    </SheetCell>
                    <SheetCell>
                      <div className="flex flex-col gap-1">
                        {showTimingOptions || newDepType !== 'FS' ? (
                        <select
                          aria-label="Link timing"
                          value={newDepType}
                          onChange={(e) => setNewDepType(e.target.value as DependencyTypeEnum)}
                          className="border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-0.5 text-xs bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]"
                        >
                          {(Object.keys(DEP_TYPE_LABEL) as DependencyTypeEnum[]).map((t) => (
                            <option key={t} value={t} title={DEPENDENCY_TIMING_LABELS[t].description}>
                              {DEP_TYPE_LABEL[t]}
                            </option>
                          ))}
                        </select>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setShowTimingOptions(true)}
                            title={DEPENDENCY_TIMING_LABELS.FS.description}
                            className="self-start text-xs text-[var(--text-ter)] underline hover:text-[var(--text-sec)]"
                          >
                            {DEP_TYPE_LABEL.FS} · More timing options
                          </button>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={onSaveDep}
                            disabled={savingDep || !newDepFrom || !newDepTo}
                            className="flex items-center gap-1 text-xs font-medium text-primary-700 disabled:opacity-40"
                          >
                            {savingDep ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                            Save
                          </button>
                          <button type="button" onClick={() => setAddingDep(false)} className="text-xs text-[var(--text-ter)]">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        {depError && <p className="text-xs text-[var(--coral)]">{depError}</p>}
                      </div>
                    </SheetCell>
                    <SheetCell>
                      <select
                        value={newDepTo}
                        onChange={(e) => setNewDepTo(e.target.value)}
                        className="border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-0.5 text-xs bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] w-full max-w-xs"
                      >
                        <option value="">To work unit…</option>
                        {workUnits
                          .filter((wu) => wu.id !== newDepFrom)
                          .map((wu) => (
                            <option key={wu.id} value={wu.id}>{wu.title}</option>
                          ))}
                      </select>
                    </SheetCell>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Sheet: Capacity Model ────────────────────────────────────────────────────

function CapacitySheet({
  rows,
  totalCapacityHours,
  committedEffortHours,
  loading,
  addingCap,
  newCapResource,
  newCapPct,
  newCapStart,
  newCapEnd,
  savingCap,
  capError,
  setAddingCap,
  setNewCapResource,
  setNewCapPct,
  setNewCapStart,
  setNewCapEnd,
  onSaveCap,
}: {
  rows: CapacityAllocation[];
  totalCapacityHours: number;
  committedEffortHours: number;
  loading: boolean;
  addingCap: boolean;
  newCapResource: string;
  newCapPct: string;
  newCapStart: string;
  newCapEnd: string;
  savingCap: boolean;
  capError: string | null;
  setAddingCap: (v: boolean) => void;
  setNewCapResource: (v: string) => void;
  setNewCapPct: (v: string) => void;
  setNewCapStart: (v: string) => void;
  setNewCapEnd: (v: string) => void;
  onSaveCap: () => void;
}) {
  const feasible = committedEffortHours <= totalCapacityHours || totalCapacityHours === 0;

  return (
    <div className="space-y-3">
      {/* Feasibility summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total Alloc. %', value: `${totalCapacityHours.toFixed(0)}%` },
          { label: 'Committed hours', value: committedEffortHours.toFixed(1) },
          {
            label: 'Feasibility',
            value: totalCapacityHours === 0 ? 'No capacity set' : feasible ? 'Within capacity' : 'Over capacity',
            accent: totalCapacityHours === 0 ? '' : feasible ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)] dark:text-[var(--coral)]',
          },
        ].map((stat) => (
          <div key={stat.label} className="border border-[var(--rule)] dark:border-[var(--rule)] px-4 py-3">
            <p className="text-[11px] uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1">{stat.label}</p>
            <p className={`text-lg font-semibold ${stat.accent ?? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
        <div className="px-4 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] flex items-center justify-between">
          <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">
            Capacity Allocations — {rows.length} owners
          </span>
          <button
            type="button"
            onClick={() => setAddingCap(true)}
            disabled={addingCap}
            className="flex items-center gap-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
            Add owner
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <SheetHeader>Owner / Resource</SheetHeader>
                <SheetHeader className="text-right">Allocation %</SheetHeader>
                <SheetHeader>Window start</SheetHeader>
                <SheetHeader>Window end</SheetHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              {loading ? (
                <EmptySheet message="Loading capacity…" />
              ) : rows.length === 0 && !addingCap ? (
                <EmptySheet message="No capacity allocations yet. Add owners to model feasibility." />
              ) : (
                <>
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]">
                      <SheetCell>
                        <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{row.resource_identifier}</span>
                      </SheetCell>
                      <SheetCell className="text-right font-mono tabular-nums">
                        {row.allocation_percentage}%
                      </SheetCell>
                      <SheetCell>
                        {row.time_window_start
                          ? new Date(row.time_window_start).toLocaleDateString()
                          : <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">—</span>}
                      </SheetCell>
                      <SheetCell>
                        {row.time_window_end
                          ? new Date(row.time_window_end).toLocaleDateString()
                          : <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">—</span>}
                      </SheetCell>
                    </tr>
                  ))}
                  {addingCap && (
                    <tr className="bg-primary-50 dark:bg-primary-900/10">
                      <SheetCell>
                        <input
                          autoFocus
                          placeholder="Owner / resource identifier"
                          value={newCapResource}
                          onChange={(e) => setNewCapResource(e.target.value)}
                          className="w-full border-b border-[var(--rule)] dark:border-[var(--rule)] bg-transparent py-0.5 text-sm focus:outline-none focus:border-primary-500"
                        />
                      </SheetCell>
                      <SheetCell className="text-right">
                        <input
                          type="number"
                          placeholder="%"
                          value={newCapPct}
                          onChange={(e) => setNewCapPct(e.target.value)}
                          className="w-16 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-transparent py-0.5 text-xs text-right focus:outline-none"
                        />
                      </SheetCell>
                      <SheetCell>
                        <input
                          type="date"
                          value={newCapStart}
                          onChange={(e) => setNewCapStart(e.target.value)}
                          className="border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-0.5 text-xs bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]"
                        />
                      </SheetCell>
                      <SheetCell>
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={newCapEnd}
                            onChange={(e) => setNewCapEnd(e.target.value)}
                            className="border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-0.5 text-xs bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]"
                          />
                          <button
                            type="button"
                            onClick={onSaveCap}
                            disabled={savingCap || !newCapResource || !newCapPct}
                            className="flex items-center gap-1 text-xs font-medium text-primary-700 disabled:opacity-40"
                          >
                            {savingCap ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          </button>
                          <button type="button" onClick={() => setAddingCap(false)} className="text-xs text-[var(--text-ter)]">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {capError && <p className="mt-1 text-xs text-[var(--coral)]">{capError}</p>}
                      </SheetCell>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Sheet: Plan of Record ─────────────────────────────────────────────────────

function PlanOfRecordSheet({
  initiatives,
  itemTitles,
  wuByInitiative,
  loading,
  cycle,
}: {
  initiatives: Initiative[];
  itemTitles: Record<string, string>;
  wuByInitiative: Map<string, WorkUnit[]>;
  loading: boolean;
  cycle: PlanCycle;
  planVersionId: string | null;
}) {
  const totalCommitted = useMemo(
    () =>
      initiatives
        .filter((init) => init.status !== 'dropped')
        .reduce(
          (sum, init) =>
            sum +
            (wuByInitiative.get(init.id) ?? []).reduce((s, wu) => s + (wu.estimated_effort_hours ?? 0), 0),
          0
        ),
    [initiatives, wuByInitiative]
  );

  const inCount = initiatives.filter((i) => i.status !== 'dropped').length;
  const droppedCount = initiatives.filter((i) => i.status === 'dropped').length;

  return (
    <div className="space-y-3">
      {/* Quarter summary bar */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Cycle', value: cycle.name },
          { label: 'Committed initiatives', value: String(inCount) },
          { label: 'Dropped / deferred', value: String(droppedCount) },
          { label: 'Total committed hours', value: totalCommitted.toFixed(1) },
        ].map((stat) => (
          <div key={stat.label} className="border border-[var(--rule)] dark:border-[var(--rule)] px-4 py-3">
            <p className="text-[11px] uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1">{stat.label}</p>
            <p className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
        <div className="px-4 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] flex items-center justify-between">
          <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">
            Committed work
          </span>
          <Link
            to="/planning/plan-of-record"
            className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
          >
            Full POR view <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <SheetHeader className="w-10">#</SheetHeader>
                <SheetHeader>Priority / Initiative</SheetHeader>
                <SheetHeader>Status</SheetHeader>
                <SheetHeader>Work Profile</SheetHeader>
                <SheetHeader className="text-right">Work units</SheetHeader>
                <SheetHeader className="text-right">Effort (hrs)</SheetHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              {loading ? (
                <EmptySheet message="Loading plan of record…" />
              ) : initiatives.length === 0 ? (
                <EmptySheet message="No initiatives in this plan version. Authorize a Plan of Record to see committed work." />
              ) : (
                initiatives.map((init) => {
                  const wus = wuByInitiative.get(init.id) ?? [];
                  const effortHours = wus.reduce((s, wu) => s + (wu.estimated_effort_hours ?? 0), 0);
                  const title = init.title ?? (init.item_id ? itemTitles[init.item_id] : null) ?? '—';
                  return (
                    <tr
                      key={init.id}
                      className={`hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] ${init.status === 'dropped' ? 'opacity-50' : ''}`}
                    >
                      <SheetCell className="text-[var(--text-ter)] font-mono">{init.priority_position ?? '—'}</SheetCell>
                      <SheetCell>
                        <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{title}</span>
                      </SheetCell>
                      <SheetCell>
                        <Badge
                          label={(init.status ?? 'planned').replace(/_/g, ' ')}
                          className={
                            init.status === 'in_progress'
                              ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                              : init.status === 'complete'
                              ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                              : init.status === 'dropped'
                              ? 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
                              : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                          }
                        />
                      </SheetCell>
                      <SheetCell>
                        {init.work_profile ? (
                          <Badge label={init.work_profile} className="bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]" />
                        ) : (
                          <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">—</span>
                        )}
                      </SheetCell>
                      <SheetCell className="text-right font-mono tabular-nums text-[var(--text-ter)]">
                        {wus.length}
                      </SheetCell>
                      <SheetCell className="text-right font-mono tabular-nums font-medium">
                        {effortHours > 0 ? effortHours.toFixed(1) : '—'}
                      </SheetCell>
                    </tr>
                  );
                })
              )}
            </tbody>
            {initiatives.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]">
                  <td colSpan={4} className="px-3 py-2 text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    Total committed
                  </td>
                  <td className="px-3 py-2 text-right text-xs font-semibold font-mono tabular-nums text-[var(--text-sec)]">
                    {initiatives.reduce((s, i) => s + (wuByInitiative.get(i.id)?.length ?? 0), 0)}
                  </td>
                  <td className="px-3 py-2 text-right text-sm font-semibold font-mono tabular-nums text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {totalCommitted.toFixed(1)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
